// ./src/components/features/Forms/FormFieldsEditModal/EmailFieldOptionsEditor.tsx
import React from "react";
import { Stack, Switch, Select, TagsInput, Text, Alert } from "@mantine/core";
import { AppIcon } from "@/components/generic";
import { EmailFieldOptions } from "./fieldOptionsTypes";

export interface EmailFieldOptionsEditorProps {
  value: EmailFieldOptions;
  onChange: (options: EmailFieldOptions) => void;
}

const PLUS_ADDRESSING_OPTIONS = [
  { value: "allow", label: "Allow (default)" },
  { value: "forbid", label: "Reject emails with +" },
  { value: "strip", label: "Strip +label before storing" },
];

// Common disposable email domains for quick-add
const COMMON_DISPOSABLE_DOMAINS = [
  "tempmail.com",
  "throwaway.email",
  "guerrillamail.com",
  "mailinator.com",
  "10minutemail.com",
  "yopmail.com",
];

export const EmailFieldOptionsEditor: React.FC<
  EmailFieldOptionsEditorProps
> = ({ value, onChange }) => {
  const handleChange = (field: keyof EmailFieldOptions, fieldValue: any) => {
    onChange({
      ...value,
      [field]: fieldValue,
    });
  };

  const handleDomainsChange = (
    field: "blockedDomains" | "allowedDomains",
    domains: string[]
  ) => {
    const normalized = domains
      .map((d) => d.trim().toLowerCase())
      .filter((d) => d);
    onChange({
      ...value,
      [field]: normalized.length > 0 ? normalized : undefined,
    });
  };

  const hasAllowedDomains =
    value.allowedDomains && value.allowedDomains.length > 0;
  const hasBlockedDomains =
    value.blockedDomains && value.blockedDomains.length > 0;

  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed">
        Configure advanced email validation options.
      </Text>

      {/* Domain Validation */}
      <Switch
        label="Verify domain exists"
        description="Check that the email domain has valid DNS records"
        checked={value.validateDomainExists ?? false}
        onChange={(e) =>
          handleChange("validateDomainExists", e.currentTarget.checked)
        }
        size="sm"
      />

      <Switch
        label="Verify domain can receive email"
        description="Check that the domain has MX records configured"
        checked={value.validateDomainAcceptsEmail ?? false}
        onChange={(e) =>
          handleChange("validateDomainAcceptsEmail", e.currentTarget.checked)
        }
        size="sm"
      />

      {/* Plus Addressing */}
      <Select
        label="Plus addressing"
        description="Control emails like user+label@domain.com"
        data={PLUS_ADDRESSING_OPTIONS}
        value={value.plusAddressing ?? "allow"}
        onChange={(val) =>
          handleChange("plusAddressing", val === "allow" ? undefined : val)
        }
        size="sm"
      />

      {/* Allowed Domains */}
      <TagsInput
        label="Allowed domains"
        placeholder="company.com"
        description="Only accept emails from these domains (leave empty for all)"
        value={value.allowedDomains ?? []}
        onChange={(domains) => handleDomainsChange("allowedDomains", domains)}
        disabled={hasBlockedDomains}
        size="sm"
      />

      {/* Blocked Domains */}
      <TagsInput
        label="Blocked domains"
        placeholder="tempmail.com"
        description="Reject emails from these domains"
        value={value.blockedDomains ?? []}
        onChange={(domains) => handleDomainsChange("blockedDomains", domains)}
        disabled={hasAllowedDomains}
        size="sm"
      />

      {hasAllowedDomains && (
        <Alert
          icon={<AppIcon iconName="InfoCircle" size={16} />}
          color="blue"
          variant="light"
        >
          <Text size="xs">
            Blocked domains is disabled when allowed domains is set.
          </Text>
        </Alert>
      )}

      {!hasAllowedDomains && !hasBlockedDomains && (
        <Text size="xs" c="dimmed">
          Common disposable domains:{" "}
          {COMMON_DISPOSABLE_DOMAINS.slice(0, 3).join(", ")}...
        </Text>
      )}
    </Stack>
  );
};
